// =============================================================================
// VandieZ shipper-web -- Profile (/profile)
// =============================================================================
// GET /users/me to render identity + business profile + tax IDs; PATCH
// /users/me to save edits. The backend dispatches the schema by
// user.primary_role -- shippers + owners share the same UserUpdateShipper
// surface so the form is identical for both.
//
// Driver-specific fields (blood group, emergency contact, etc.) are
// intentionally omitted -- the shipper-web only ever serves shippers
// and owners.
// =============================================================================

import { AlertCircle, Loader2, Save } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";

import { profileApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { ApiError } from "@/types/auth";
import type {
  EntityType,
  UserMeResponse,
  UserUpdateShipperPayload,
} from "@/types/user";

const ENTITY_TYPE_OPTIONS: { value: EntityType; label: string }[] = [
  { value: "individual", label: "Individual" },
  { value: "proprietorship", label: "Proprietorship" },
  { value: "partnership", label: "Partnership" },
  { value: "private_limited", label: "Private limited" },
  { value: "public_limited", label: "Public limited" },
  { value: "llp", label: "LLP" },
  { value: "huf", label: "HUF" },
  { value: "trust", label: "Trust" },
];

interface FormState {
  full_name: string;
  email: string;
  district: string;
  state: string;
  address_pincode: string;
  entity_type: EntityType | "";
  business_name: string;
  business_address: string;
  gstin: string;
  pan: string;
}

function emptyForm(): FormState {
  return {
    full_name: "",
    email: "",
    district: "",
    state: "",
    address_pincode: "",
    entity_type: "",
    business_name: "",
    business_address: "",
    gstin: "",
    pan: "",
  };
}

function hydrate(me: UserMeResponse): FormState {
  return {
    full_name: me.full_name,
    email: me.email ?? "",
    district: me.district ?? "",
    state: me.state,
    address_pincode: me.address_pincode ?? "",
    entity_type: me.entity_type ?? "",
    business_name: me.business_name ?? "",
    business_address: me.business_address ?? "",
    gstin: me.gstin ?? "",
    pan: me.pan ?? "",
  };
}

export function ProfilePage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const toast = useToast();

  const [me, setMe] = useState<UserMeResponse | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (accessToken === null) {return;}
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    profileApi
      .me(accessToken, controller.signal)
      .then((data) => {
        setMe(data);
        setForm(hydrate(data));
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {return;}
        setError(
          err instanceof ApiError ? err.problem.detail : "Couldn't load profile.",
        );
        setIsLoading(false);
      });
    return () => { controller.abort(); };
  }, [accessToken]);

  function update<K extends keyof FormState>(field: K, value: FormState[K]): void {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (accessToken === null) {return;}
    setIsSaving(true);
    setError(null);

    const payload: UserUpdateShipperPayload = {
      full_name: form.full_name.trim(),
      email: form.email.trim().length === 0 ? null : form.email.trim(),
      district:
        form.district.trim().length === 0 ? null : form.district.trim(),
      state: form.state.trim().length === 0 ? null : form.state.trim(),
      address_pincode:
        form.address_pincode.trim().length === 0
          ? null
          : form.address_pincode.trim(),
      entity_type: form.entity_type === "" ? null : form.entity_type,
      business_name:
        form.business_name.trim().length === 0
          ? null
          : form.business_name.trim(),
      business_address:
        form.business_address.trim().length === 0
          ? null
          : form.business_address.trim(),
      gstin:
        form.gstin.trim().length === 0 ? null : form.gstin.trim().toUpperCase(),
      pan: form.pan.trim().length === 0 ? null : form.pan.trim().toUpperCase(),
    };

    try {
      const updated = await profileApi.update(accessToken, payload);
      setMe(updated);
      setForm(hydrate(updated));
      toast.success("Profile saved.");
    } catch (err: unknown) {
      const detail =
        err instanceof ApiError ? err.problem.detail : "Couldn't save profile.";
      setError(detail);
      toast.error(detail);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Profile"
        description="Identity, business details, and tax IDs we surface to drivers."
      />

      {error !== null ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {isLoading || me === null ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Identity</CardTitle>
              <CardDescription>
                Phone {me.phone} · KYC tier {me.kyc_tier.replace(/_/g, " ")}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="full_name">Full name</Label>
                <Input
                  id="full_name"
                  value={form.full_name}
                  onChange={(e) => { update("full_name", e.target.value); }}
                  required
                  maxLength={200}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => { update("email", e.target.value); }}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Address</CardTitle>
              <CardDescription>
                Used for invoicing and shipper-side trip notifications.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="district">District</Label>
                <Input
                  id="district"
                  value={form.district}
                  onChange={(e) => { update("district", e.target.value); }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={form.state}
                  onChange={(e) => { update("state", e.target.value); }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="address_pincode">PIN code</Label>
                <Input
                  id="address_pincode"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="6-digit"
                  value={form.address_pincode}
                  onChange={(e) => { update("address_pincode", e.target.value); }}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Business</CardTitle>
              <CardDescription>
                Goes on invoices, shown to drivers on accepted loads.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="entity_type">Entity type</Label>
                <select
                  id="entity_type"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={form.entity_type}
                  onChange={(e) => { update("entity_type", e.target.value as EntityType | ""); }}
                >
                  <option value="">Select…</option>
                  {ENTITY_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="business_name">Business name</Label>
                <Input
                  id="business_name"
                  value={form.business_name}
                  onChange={(e) => { update("business_name", e.target.value); }}
                  maxLength={200}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="business_address">Business address</Label>
                <Textarea
                  id="business_address"
                  rows={3}
                  value={form.business_address}
                  onChange={(e) => { update("business_address", e.target.value); }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="gstin">GSTIN</Label>
                <Input
                  id="gstin"
                  value={form.gstin}
                  onChange={(e) => { update("gstin", e.target.value.toUpperCase()); }}
                  placeholder="15-character GSTIN"
                  maxLength={15}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pan">PAN</Label>
                <Input
                  id="pan"
                  value={form.pan}
                  onChange={(e) => { update("pan", e.target.value.toUpperCase()); }}
                  placeholder="10-character PAN"
                  maxLength={10}
                  className="font-mono"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save changes
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
